import type { MechanicDefinition } from "./types";

export const characterClasses: MechanicDefinition = {
  id: "character_classes",
  disabled: true,
  name: "Character Classes & Levels",
  description: "D&D-style class system with level progression, XP, and class abilities",
  icon: "GraduationCap",
  category: "systems",
  aiHints: {
    purpose: "Provides D&D-style class selection and XP-based leveling with ability unlocks at milestones.",
    auditPasses: 1,
    whenToUse: "Any RPG world where characters have distinct archetypes that evolve through play.",
    synergies: ["skill_checks", "combat", "magic_system", "character_progression"],
    conflicts: ["character_progression"],
    prerequisites: [],
    tags: ["classes", "levels", "xp", "progression", "abilities", "d&d"],
    complexity: 2,
    exampleConfig: { level_cap: 20, xp_curve: "standard", multiclass: false },
    genrePresets: {
      "classic_dnd": { level_cap: 20, xp_curve: "standard", multiclass: true },
      "quick_campaign": { level_cap: 10, xp_curve: "fast", multiclass: false },
      "epic": { level_cap: 30, xp_curve: "slow", multiclass: true },
    },
    stateVariables: ["player_level", "player_xp", "player_class", "class_abilities"],
  },
  configSchema: [
    {
      key: "level_cap",
      label: "Max Level",
      type: "number",
      default: 20,
      description: "Maximum character level",
    },
    {
      key: "xp_curve",
      label: "XP Curve",
      type: "select",
      options: [
        { value: "fast", label: "Fast (level up often)" },
        { value: "standard", label: "Standard (D&D-like pacing)" },
        { value: "slow", label: "Slow (rare, meaningful levels)" },
      ],
      default: "standard",
    },
    {
      key: "multiclass",
      label: "Allow Multiclassing",
      type: "boolean",
      default: false,
      description: "Can players take levels in multiple classes",
    },
  ],
  systemPromptSection: (config) => {
    const levelCap = config.level_cap || 20;
    const xpCurve = config.xp_curve || "standard";
    const multi = config.multiclass ? "Multiclassing is allowed." : "Single class only.";
    const xpTable: Record<string, string> = {
      fast: "XP needed per level: level × 50. Players level frequently.",
      standard: "XP needed per level: level × 100. Balanced progression.",
      slow: "XP needed per level: level × 200. Each level is a milestone.",
    };
    return `## CHARACTER CLASSES & LEVELS (Hard Mechanic)
- Level cap: ${levelCap}. ${multi}
- ${xpTable[xpCurve]}
- Track player_level, player_xp, player_class, and class_abilities in state variables.
- Use the award_xp tool when the player earns XP (from combat, quests, exploration). It auto-detects level-ups.
- On level up: announce it dramatically, grant new class ability appropriate to class, increase HP/stats.
- Classic D&D classes available: Fighter, Wizard, Rogue, Cleric, Ranger, Paladin, Bard, Warlock, Druid, Barbarian, Monk, Sorcerer — or custom classes fitting the world.
- Each class has a distinct playstyle. Class abilities unlock at levels 1, 3, 5, 7, 10, 15, 20.
- Award XP generously for creative solutions, not just combat kills.`;
  },
  widgets: [
    {
      id: "xp_status",
      type: "status_bar_item",
      label: "Level",
      icon: "GraduationCap",
      component: "XPStatus",
      dataBindings: {
        level: "variables.player_level",
        xp: "variables.player_xp",
        playerClass: "variables.player_class",
      },
      visibility: "when_data",
      priority: 3,
    },
  ],
  initializeState: (config) => ({
    player_level: 1,
    player_xp: 0,
    player_class: null,
    class_abilities: [],
    _xp_curve: config.xp_curve || "standard",
  }),
  // React to combat kills and quest completions to auto-award XP
  onEvent: (event) => {
    if (event.type === "combat_kill") {
      const xp = event.data.xp_value || 25;
      return {
        stateUpdates: { _pending_xp: xp, _pending_xp_reason: `Defeated ${event.data.target}` },
        narrativeNotes: [`⚔ Auto-XP: +${xp} XP for defeating ${event.data.target}`],
      };
    }
    if (event.type === "quest_complete") {
      const xp = event.data.xp_value || 50;
      return {
        stateUpdates: { _pending_xp: xp, _pending_xp_reason: `Completed quest: ${event.data.quest_name}` },
        narrativeNotes: [`📜 Auto-XP: +${xp} XP for completing "${event.data.quest_name}"`],
      };
    }
    return null;
  },
  tools: [
    {
      type: "function",
      function: {
        name: "award_xp",
        description: "Award experience points to the player. Automatically handles level-up detection.",
        parameters: {
          type: "object",
          properties: {
            amount: { type: "number", description: "XP to award" },
            reason: { type: "string", description: "What earned the XP (combat, quest, creativity, etc.)" },
            source: { type: "string", enum: ["combat", "quest", "exploration", "roleplay", "puzzle", "other"] },
          },
          required: ["amount", "reason"],
        },
      },
    },
  ],
};
